/**
 * LocationMapPicker
 *
 * A compact, self-contained map widget for picking a location.
 * Mirrors the click-to-pin + Nominatim reverse-geocode pattern from MapView.js.
 *
 * Props:
 *   position  – [lat, lng] | null   – controlled pin position
 *   onPick    – ({ lat, lng, address }) => void
 *   onClear   – () => void
 */
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap, ZoomControl } from 'react-leaflet';
import * as Leaflet from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/solid';

// Default centre – Zurich, matching MapView.js
const ZURICH = [47.3717597, 8.5426102];

// Purple pin that mirrors MapView's pendingIcon
const PIN_ICON = Leaflet.divIcon({
  className: '',
  html: `<div style="width:36px;height:36px;background:linear-gradient(135deg,#1d5cab,#2372bd);border-radius:50%;border:3px solid white;box-shadow:0 0 0 3px #1d5cab44,0 4px 10px rgba(29,92,171,0.4);display:flex;align-items:center;justify-content:center"><span style="color:white;font-size:16px">📍</span></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

// ─── Inner helpers (mirror LocationSelector + MapRecenter from MapView.js) ─────

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const Recenter = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 1 });
  }, [position, map]);
  return null;
};

// ─── Main component ────────────────────────────────────────────────────────────

const LocationMapPicker = ({ position, onPick, onClear }) => {
  // Internal copy so the map can re-centre without waiting for parent re-render
  const [pinPos, setPinPos] = useState(position || null);
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  // Sync if parent clears the pin externally
  useEffect(() => {
    if (!position) { setPinPos(null); setQuery(''); }
    else setPinPos(position);
  }, [position]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`
      );
      const data = await res.json();
      return data?.display_name || '';
    } catch (_) { return ''; }
  };

  const handleMapPick = async (lat, lng) => {
    setPinPos([lat, lng]);
    const address = await reverseGeocode(lat, lng);
    if (address) setQuery(address);
    onPick({ lat, lng, address });
  };

  const handleSearch = async () => {
    const q = query.trim();
    if (!q) return;
    setIsSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q)}&limit=1`
      );
      const data = await res.json();
      if (data?.length > 0) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        setPinPos([lat, lng]);
        const address = data[0].display_name;
        setQuery(address);
        onPick({ lat, lng, address });
      }
    } catch (_) {}
    setIsSearching(false);
  };

  const handleClear = () => {
    setPinPos(null);
    setQuery('');
    onClear();
  };

  return (
    <div>
      {/* Search row */}
      <div className="flex gap-1.5 mb-2">
        <input
          className="flex-1 border border-slate-200 p-2 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-400"
          placeholder="Search address…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
        />
        <button
          type="button"
          onClick={handleSearch}
          disabled={isSearching}
          className="p-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 transition-colors"
          aria-label="Search"
        >
          <MagnifyingGlassIcon className="h-4 w-4" />
        </button>
        {pinPos && (
          <button
            type="button"
            onClick={handleClear}
            className="p-2 bg-slate-100 text-slate-500 rounded-lg hover:bg-slate-200 transition-colors"
            aria-label="Clear pin"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-slate-200 relative" style={{ height: 200 }}>
        <MapContainer
          center={pinPos || ZURICH}
          zoom={pinPos ? 15 : 12}
          zoomControl={false}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY}`}
          />
          <ZoomControl position="bottomright" />
          <ClickHandler onPick={handleMapPick} />
          <Recenter position={pinPos} />
          {pinPos && <Marker position={pinPos} icon={PIN_ICON} />}
        </MapContainer>

        {/* "Tap to pin" overlay — only when no pin set yet */}
        {!pinPos && (
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ zIndex: 1000 }}
          >
            <span className="bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs font-medium text-slate-500 shadow-sm border border-slate-100">
              Tap the map to pin a location
            </span>
          </div>
        )}
      </div>

      {/* Coordinates readout */}
      {pinPos && (
        <p className="text-[10px] text-slate-400 mt-1 text-right">
          {pinPos[0].toFixed(5)}, {pinPos[1].toFixed(5)}
        </p>
      )}
    </div>
  );
};

export default LocationMapPicker;
